import { Link } from "react-router-dom";
import Nav from "../../components/Nav";
import Footer from "../../components/Footer";
import Reveal from "../../components/Reveal";

const ACTIVITIES = [
  ["Preventive Health Workshops", "Sessions on hygiene, common preventable illnesses, and early warning signs, run in partnership with local health workers for students and community members."],
  ["Environmental Hygiene Campaigns", "On-campus and community clean-up and hygiene-awareness drives targeting the conditions that let preventable disease spread."],
  ["Workplace Safety Talks", "Practical sessions on hazard awareness and basic safety practices for student groups preparing for internships and fieldwork."],
  ["Health Worker Partnerships", "Coordinated outreach visits and joint programming with local health workers, giving ambassadors direct clinical grounding for the material they teach."],
];

const PROCESS = [
  ["Founding", "Identified a gap in peer-to-peer preventive health education on campus and proposed the initiative to coursemates and local health contacts."],
  ["Recruitment", "Recruited and onboarded 20 coursemates as ambassadors, each responsible for a strand of outreach: hygiene, safety, or community liaison."],
  ["Program Design", "Built a rotating schedule of workshops and outreach events, designed alongside local health workers to stay grounded in real community needs."],
  ["Ongoing Delivery", "Continue running workshops and outreach programs, adapting topics each term based on what the community is actually asking about."],
];

export default function ProjectCommunityHealthAmbassadors() {
  return (
    <>
      <Nav />

      <div className="mx-auto max-w-content px-6 pt-32 md:px-12 md:pt-40">
        <Link to="/projects" className="text-[0.75rem] uppercase tracking-widest2 text-ink-faint hover:text-heading">
          &larr; Back to Projects
        </Link>
      </div>

      <Reveal className="mx-auto max-w-content px-6 pb-14 pt-8 md:px-12 md:pb-20">
        <p className="eyebrow">Student Leadership &middot; Community Health</p>
        <h1 className="mt-3 font-serif text-4xl font-extrabold leading-tight text-heading md:text-5xl">
          Community Health <em className="italic text-gold-500">Ambassadors</em>
        </h1>
        <p className="mt-6 max-w-[62ch] text-[1rem] leading-relaxed text-ink-soft">
          A student-led public health initiative promoting preventive health practices, environmental
          hygiene, and workplace safety, recruiting and coordinating 20 coursemates to run workshops and
          outreach programs in collaboration with local health workers.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4">
          <a
            href="https://chagh.ellenboakye.online"
            target="_blank"
            rel="noreferrer"
            className="border border-forest-900 bg-forest-900 px-6 py-3 text-[0.75rem] uppercase tracking-widest2 text-white transition-colors hover:bg-forest-700"
          >
            Visit Site
          </a>
        </div>
      </Reveal>

      <Reveal as="section" className="border-y border-line bg-surface-alt">
        <div className="mx-auto grid max-w-content grid-cols-2 divide-x divide-line px-6 sm:grid-cols-4 md:px-12">
          <div className="px-2 py-8 text-center first:pl-0 sm:px-6">
            <div className="font-serif text-3xl text-heading">20</div>
            <div className="mt-1 text-[0.75rem] uppercase tracking-widest2 text-ink-faint">Coursemates Coordinated</div>
          </div>
          <div className="px-2 py-8 text-center sm:px-6">
            <div className="font-serif text-3xl text-heading">4</div>
            <div className="mt-1 text-[0.75rem] uppercase tracking-widest2 text-ink-faint">Focus Areas</div>
          </div>
          <div className="px-2 py-8 text-center sm:px-6">
            <div className="font-serif text-3xl text-heading">2023</div>
            <div className="mt-1 text-[0.75rem] uppercase tracking-widest2 text-ink-faint">Founded</div>
          </div>
          <div className="px-2 py-8 text-center sm:px-6">
            <div className="font-serif text-3xl text-heading">Ongoing</div>
            <div className="mt-1 text-[0.75rem] uppercase tracking-widest2 text-ink-faint">Status</div>
          </div>
        </div>
      </Reveal>

      <div className="mx-auto max-w-content px-6 py-16 md:px-12 md:py-24">
        <div className="grid gap-14 md:grid-cols-[1fr_320px] md:gap-16">
          <div>
            <Reveal className="mb-16">
              <p className="eyebrow">Overview</p>
              <h2 className="mt-2 font-serif text-2xl text-heading">Why I founded it</h2>
              <p className="mt-4 max-w-[62ch] text-[0.95rem] leading-relaxed text-ink-soft">
                A lot of preventive health knowledge never reaches the people who need it most, not because
                it&rsquo;s complicated, but because it&rsquo;s usually delivered by someone the audience
                doesn&rsquo;t know or trust. I started this initiative to close that gap with peers, students
                teaching students, in language and settings people already feel comfortable in.
              </p>
              <p className="mt-4 max-w-[62ch] text-[0.95rem] leading-relaxed text-ink-soft">
                Working alongside local health workers kept the content grounded in what the community was
                actually dealing with, rather than a generic curriculum handed down from elsewhere.
              </p>
            </Reveal>

            <Reveal className="mb-16">
              <p className="eyebrow">Programs</p>
              <h2 className="mt-2 font-serif text-2xl text-heading">What ambassadors run</h2>
              <div className="mt-6 divide-y divide-line border-t border-line">
                {ACTIVITIES.map(([title, desc]) => (
                  <div key={title} className="grid gap-1 py-5 md:grid-cols-[220px_1fr] md:gap-8">
                    <p className="font-serif text-base text-heading">{title}</p>
                    <p className="text-[0.9rem] leading-relaxed text-ink-soft">{desc}</p>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal>
              <p className="eyebrow">Process</p>
              <h2 className="mt-2 font-serif text-2xl text-heading">How it came together</h2>
              <div className="mt-6 space-y-6">
                {PROCESS.map(([title, desc], i) => (
                  <div key={title} className="flex gap-5 border-t border-line pt-5">
                    <span className="font-serif text-lg text-ink-faint">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <p className="font-serif text-base text-heading">{title}</p>
                      <p className="mt-1 text-[0.9rem] leading-relaxed text-ink-soft">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <Reveal as="aside" className="h-fit space-y-6 md:sticky md:top-28">
            <div className="border border-line-strong p-6">
              <p className="eyebrow mb-4">Project Info</p>
              <dl className="space-y-3 text-[0.85rem]">
                <div><dt className="text-ink-faint">Role</dt><dd className="text-heading">Founder &amp; Coordinator</dd></div>
                <div><dt className="text-ink-faint">Type</dt><dd className="text-heading">Student-Led Public Health Initiative</dd></div>
                <div><dt className="text-ink-faint">Team</dt><dd className="text-heading">20 Coursemates</dd></div>
                <div><dt className="text-ink-faint">Duration</dt><dd className="text-heading">Aug 2023 – Present</dd></div>
              </dl>
            </div>
            <div className="border border-line-strong p-6">
              <p className="eyebrow mb-4">Links</p>
              <a href="https://chagh.ellenboakye.online" target="_blank" rel="noreferrer" className="block text-[0.75rem] uppercase tracking-widest2 text-link hover:text-heading">
                chagh.ellenboakye.online &rarr;
              </a>
            </div>
          </Reveal>
        </div>
      </div>

      <Reveal as="section" className="bg-forest-900">
        <div className="mx-auto max-w-content px-6 py-16 md:px-12 md:py-24">
          <p className="text-[0.75rem] uppercase tracking-widest2 text-gold-300">Why it matters</p>
          <h2 className="mt-2 font-serif text-2xl font-extrabold text-white md:text-3xl">The impact of peer-led health education</h2>
          <div className="mt-10 grid gap-px border-y border-white/15 sm:grid-cols-3">
            <div className="border-t border-white/15 py-6 pr-6 sm:border-t-0">
              <div className="font-serif text-2xl text-white">Trust</div>
              <p className="mt-2 text-[0.85rem] leading-relaxed text-forest-50/75">
                Peer educators are more approachable than unfamiliar outsiders, especially on sensitive health topics.
              </p>
            </div>
            <div className="border-t border-white/15 py-6 sm:border-t-0 sm:px-6">
              <div className="font-serif text-2xl text-white">Reach</div>
              <p className="mt-2 text-[0.85rem] leading-relaxed text-forest-50/75">
                20 ambassadors multiply the initiative&rsquo;s reach across classes, halls, and communities far beyond one coordinator.
              </p>
            </div>
            <div className="border-t border-white/15 py-6 pl-0 sm:border-t-0 sm:pl-6">
              <div className="font-serif text-2xl text-white">Continuity</div>
              <p className="mt-2 text-[0.85rem] leading-relaxed text-forest-50/75">
                A rotating ambassador model means the program keeps running as members graduate and new ones join.
              </p>
            </div>
          </div>
        </div>
      </Reveal>

      <section className="border-t border-line">
        <div className="mx-auto flex max-w-content flex-wrap items-center justify-between gap-6 px-6 py-14 md:px-12">
          <p className="font-serif text-xl text-heading">See all projects</p>
          <Link
            to="/projects"
            className="border border-forest-900 bg-forest-900 px-6 py-3 text-[0.75rem] uppercase tracking-widest2 text-white transition-colors hover:bg-forest-700"
          >
            Browse Projects &rarr;
          </Link>
        </div>
      </section>

      <Footer />
    </>
  );
}
